import { Card } from "@/src/components/wrappers/Card";
import { Booking, Broker } from "../../../model";
import { useState } from "react";
import { Button } from "@/src/components/ui/button";
import EditCustomerEmailModal from "./EditCustomerEmailModal";
import { EditGuestsNumberModal } from "./EditGuestsNumberModal";
import { useHubspotAddress } from "@/src/hooks/useHubspotAddressData";

const UsersSection = ({ booking, broker }: { booking: Booking; broker: Broker }) => {
  const [customerEmailModalOpen, setCustomerEmailModalOpen] = useState(false);
  const [guestsModalOpen, setGuestsModalOpen] = useState(false);
  const additionalEmails = booking?.customer?.additionalEmails?.map((data: { email: string }) => data.email);
  const customerData = {
    email: booking?.customerEmail,
    emails: additionalEmails,
  };

  const hubspotId = booking?.customer?.hubspotId as string | undefined;
  const { data: hsAddress, isLoading, isError } = useHubspotAddress(hubspotId);

  const hasAnyAddress =
    !!hsAddress &&
    ["streetAddress", "postalCode", "city", "stateRegion", "countryRegion"].some(
      (k) => {
        const v = (hsAddress as Record<string, string | null>)[k];
        return typeof v === "string" && v.trim() !== "";
      }
    );

  const showAddress = !isLoading && !isError && hasAnyAddress;

  return (
    <div className="flex w-full gap-6">
      <Card className="flex flex-[3] flex-col justify-between">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Customer</h3>
          <Button onClick={() => setCustomerEmailModalOpen(true)} variant="link">
            Edit
          </Button>
          <EditCustomerEmailModal
            defaultValues={customerData}
            bookingId={booking.id}
            open={customerEmailModalOpen}
            onOpenChange={setCustomerEmailModalOpen}
          />
        </div>
        <span>
          {booking.customerFirstName} {booking.customerLastName}
        </span>
        <span>{booking.customerEmail}</span>
        <span>{booking.customerMobile}</span>
        {additionalEmails && additionalEmails?.length > 0 && (
          <>
            <span className="font-semibold text-sm mt-2">Additional emails:</span>
            {booking.customer?.additionalEmails?.map((data: { email: string }) => (
              <span key={data.email}>{data.email}</span>
            ))}
          </>
        )}
        {showAddress && <div className="mt-4">
          <span className="font-semibold text-md">Address</span>
          <div className="mt-1 text-md text-slate-700">
            {isLoading && <span>Loading…</span>}
            {isError && <span>-</span>}
            {!isLoading && !isError && (
              <>
                {hsAddress?.streetAddress && <div>Street address: <span className="font-semibold">{hsAddress?.streetAddress}</span></div>}
                {hsAddress?.postalCode && <div>Postal code: <span className="font-semibold">{hsAddress?.postalCode}</span></div>}
                {hsAddress?.city && <div>City: <span className="font-semibold">{hsAddress?.city}</span></div>}
                {hsAddress?.stateRegion && <div>State/region: <span className="font-semibold">{hsAddress?.stateRegion}</span></div>}
                {hsAddress?.countryRegion && <div>Country/region: <span className="font-semibold">{hsAddress?.countryRegion}</span></div>}
              </>
            )}
          </div>
        </div>}
        <div className="flex mt-4">
          <div className="flex w-full gap-4 items-center">
            <div className="font-medium text-md flex items-center justify-between">No. of guests</div>
            <span>{booking.guests || booking.offer?.guests}</span>
          </div>
          <div>
            <Button onClick={() => setGuestsModalOpen(true)} variant="link">
              Edit
            </Button>
            <EditGuestsNumberModal
              key={guestsModalOpen ? 1 : 0}
              onOpenChange={setGuestsModalOpen}
              open={guestsModalOpen}
              booking={booking}
              guestsNo={booking.guests || booking.offer?.guests}
            />
          </div>
        </div>
      </Card>
      <Card className="flex flex-[3] gap-x-6">
        <div className="flex flex-col">
          <span className="font-semibold text-lg mb-2">Broker</span>
          <div>
            <p>{broker?.fullName}</p>
            <p>{broker?.email}</p>
            <p>{broker?.mobile}</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default UsersSection;
